import { fetchInventorySnapshot } from "@/lib/inventorySnapshot.js";
// @/plugins/2.pinia/modules/nfts/unstakedNftsStore.js
import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { $apiV5 } from "@/plugins/axios";

export const fetchUnstakedNfts = defineStore("unstakedNfts", () => {
  // ================ STATE ================

  const unstakedNfts = ref([]); // Raw nfts data
  const isLoading = ref(false);
  const error = ref(null);
  const snapshot = ref(null);
  let requestVersion = 0;

  // ================ GETTERS ================

  /** Returns raw nfts array without any transformation * @returns {Array} */
  const unstakedNftsArray = computed(() => {
    // Simply return the raw data without transformation
    return unstakedNfts.value;
  });

  /** Returns NFTs that can be minted */
  const mintableNfts = computed(() =>
    unstakedNfts.value.filter((nft) => nft?.canMint === true)
  );

  // ================ ACTIONS ================

  /** Fetches unstaked nfts from API * @returns {Promise<Array>} Array of unstaked nfts */
  const fetchUnstakedNfts = async (
    page = 1,
    pageSize = 100,
    forceRefresh = false
  ) => {
    const request = ++requestVersion;
    isLoading.value = true;
    error.value = null;

    try {
      // Get chain based on environment
      const chain =
        import.meta.env.VITE_AUTH_PROVIDER === "privy"
          ? import.meta.env.VITE_PRIVY_CHAIN_ID === "43113"
            ? "avalancheTestnet"
            : "avalanche"
          : import.meta.env.VITE_LOCAL || import.meta.env.MODE === "production"
          ? "avalanche"
          : "avalancheTestnet";
      console.log(`Fetching nfts from chain: ${chain}`);

      const items = await fetchInventorySnapshot($apiV5.get.bind($apiV5), {
        chain, page, pageSize, forceRefresh, onSnapshot: value => { if (request === requestVersion) snapshot.value = value; }, isCurrent: () => request === requestVersion,
      });
      if (items === null || request !== requestVersion) return [];
      unstakedNfts.value = items;

      return unstakedNftsArray.value;
    } catch (err) {
      if (request !== requestVersion) return [];
      console.warn("NFT inventory could not be loaded.", {
        status: err.response?.status,
      });
      error.value = err.message || "Failed to load nfts";
      return [];
    } finally {
      if (request === requestVersion) isLoading.value = false;
    }
  };

  /** Resets the store state */
  const reset = () => {
    requestVersion++;
    unstakedNfts.value = [];
    snapshot.value = null;
    isLoading.value = false;
    error.value = null;
  };

  return {
    // State
    unstakedNfts,
    isLoading,
    error,
    snapshot,

    // Getters
    unstakedNftsArray,
    mintableNfts,

    // Actions
    fetchUnstakedNfts,
    reset,
  };
});
